import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import PaymentStatusPoller from './PaymentStatusPoller';
import './PaymentCallback.scss';

// 全局状态，防止重复处理
const globalProcessingState = {
  isProcessing: false,
  processedTokens: new Set()
};

const PaymentCallback = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState(null);
  const [isPollerActive, setIsPollerActive] = useState(false); // 控制轮询器状态
  const processingRef = useRef(false); // 防止重复处理
  const [orderId, setOrderId] = useState(null);
  const [showPoller, setShowPoller] = useState(false);

  // 组件实例标识（用于全局状态管理）
  const instanceId = React.useRef(Math.random().toString(36).substr(2, 9));

  // 初始化支付状态
  useEffect(() => {
    const initializePaymentStatus = async () => {
      // 防止重复初始化
      if (globalProcessingState.isProcessing) {
        console.log('Payment already being processed');
        return;
      }
      
      globalProcessingState.isProcessing = true;
      
      try {
        const urlParams = new URLSearchParams(location.search);
        const orderIdParam = urlParams.get('orderId');
        const paymentStatus = urlParams.get('status');
        const isSuccess = location.pathname.includes('/success');
        const isCancelled = location.pathname.includes('/cancel');
        
        console.log('Payment callback params:', { orderIdParam, paymentStatus, isSuccess, isCancelled });
        
        if (orderIdParam) {
          setOrderId(orderIdParam);
        }
        
        if (isCancelled) {
          // 处理取消的支付
          setStatus('cancelled');
          setMessage(t('payment.cancelled', 'Payment was cancelled'));
          
          // 通知后端取消支付
          if (orderIdParam) {
            try {
              await fetch(`/api/v2/payment/order/${orderIdParam}/cancel`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              });
            } catch (error) {
              console.error('Failed to notify backend about payment cancellation:', error);
            }
          }
          
          globalProcessingState.isProcessing = false;
          return;
        }
        
        if (isSuccess || paymentStatus === 'success') {
          // 成功支付，需要轮询状态
          if (orderIdParam) {
            setStatus('processing');
            setMessage(t('payment.processing', 'Processing your payment...'));
            setShowPoller(true);
            setIsPollerActive(true); // 激活轮询器
          } else {
            setStatus('success');
            setMessage(t('payment.success', 'Payment completed successfully'));
            globalProcessingState.isProcessing = false;
          }
        } else {
          // 其他情况，显示处理中状态
          setStatus('processing');
          setMessage(t('payment.processing', 'Processing your payment...'));
          
          if (orderIdParam) {
            setShowPoller(true);
            setIsPollerActive(true); // 激活轮询器
          } else {
            // 没有orderId，无法轮询，显示错误
            setStatus('error');
            setMessage(t('payment.no_order_id', 'Missing order information'));
            globalProcessingState.isProcessing = false;
          }
        }
      } catch (error) {
        console.error('Error initializing payment status:', error);
        setStatus('error');
        setMessage(t('payment.initialization_error', 'Failed to initialize payment status'));
        globalProcessingState.isProcessing = false;
      }
    };
    
    initializePaymentStatus();
    
    // 清理函数
    return () => {
      setIsPollerActive(false); // 组件卸载时停止轮询
    };
  }, [location, t]);

  // 处理轮询状态更新
  const handleStatusUpdate = React.useCallback((statusUpdate) => {
    const { status: newStatus, message: newMessage, data } = statusUpdate;
    
    // 防止在组件卸载后更新状态
    if (!instanceId.current) {
      return;
    }
    
    // 防止重复处理
    if (processingRef.current) {
      console.log('Already processing, ignoring update');
      return;
    }
    
    processingRef.current = true;
    
    try {
      switch (newStatus) {
        case 'completed':
          setStatus('success');
          setMessage(t('payment.callback.success', 'Payment completed successfully'));
          setDetails({
            transactionId: data?.gatewayTransactionId || orderId,
            status: 'completed',
            creditsAdded: data?.creditsAdded
          });
          setShowPoller(false); // 停止轮询器
          setIsPollerActive(false); // 停止轮询
          globalProcessingState.isProcessing = false; // 重置全局状态
          break;
        
        case 'quick_success':
          setStatus('quick_success');
          setMessage(t('payment.callback.quickSuccess', 'Payment successful! Your credits are being processed and will be available shortly.'));
          setDetails({
            transactionId: orderId,
            status: 'processing',
            showQuickSuccess: true
          });
          // 继续轮询，但显示成功状态
          break;
        
        case 'failed':
          setStatus('error');
          setMessage(t('payment.callback.failed', 'Payment processing failed'));
          setShowPoller(false); // 停止轮询器
          setIsPollerActive(false); // 停止轮询
          globalProcessingState.isProcessing = false; // 重置全局状态
          break;
        
        case 'timeout':
          setStatus('processing_timeout');
          setMessage(t('payment.callback.timeout', 'Payment is still being processed. Please check your account later.'));
          setShowPoller(false); // 停止轮询器
          setIsPollerActive(false); // 停止轮询
          globalProcessingState.isProcessing = false; // 重置全局状态
          break;
        
        case 'error':
          setStatus('error');
          setMessage(newMessage || t('payment.callback.error', 'An error occurred while checking payment status'));
          setShowPoller(false); // 停止轮询器
          setIsPollerActive(false); // 停止轮询
          globalProcessingState.isProcessing = false; // 重置全局状态
          break;
        
        default:
          break;
      }
    } finally {
      // 延迟重置处理标志，避免快速连续更新
      setTimeout(() => {
        processingRef.current = false;
      }, 1000);
    }
  }, [t, orderId]); // 添加依赖项并使用useCallback优化

  const handleContinue = () => {
    if (status === 'success') {
      navigate('/dashboard');
    } else {
      navigate('/pricing');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="callback-content processing">
            <div className="loading-spinner"></div>
            <h1>{t('payment.callback.processing.title', 'Processing Payment')}</h1>
            <p>{message || t('payment.callback.processing.message', 'Please wait while we verify your payment...')}</p>
            {showPoller && orderId && isPollerActive && (
              <PaymentStatusPoller
                orderId={orderId}
                onStatusUpdate={handleStatusUpdate}
                maxAttempts={15}
                interval={3000}
              />
            )}
          </div>
        );

      case 'quick_success':
        return (
          <div className="callback-content quick-success">
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#4CAF50"/>
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>{t('payment.callback.quickSuccessDetails.title', 'Payment Successful!')}</h1>
            <p>{message}</p>
            <div className="credits-processing-indicator">
              <div className="small-loading-spinner"></div>
              <p className="processing-text">{t('payment.callback.quickSuccessDetails.processing', 'Credits are being added to your account...')}</p>
            </div>
            <button className="continue-button success" onClick={handleContinue}>
              {t('payment.callback.quickSuccessDetails.continue', 'Continue to Dashboard')}
            </button>
            {showPoller && orderId && isPollerActive && (
              <PaymentStatusPoller
                orderId={orderId}
                onStatusUpdate={handleStatusUpdate}
                maxAttempts={15}
                interval={3000}
              />
            )}
          </div>
        );

      case 'processing_timeout':
        return (
          <div className="callback-content processing-timeout">
            <div className="warning-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#FF9800"/>
                <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>{t('payment.callback.timeoutDetails.title', 'Payment Processing')}</h1>
            <p>{message}</p>
            <p>{t('payment.callback.timeoutDetails.description', 'Your payment may still be processing. Please check your account in a few minutes or contact support if needed.')}</p>
            <button className="continue-button warning" onClick={handleContinue}>
              {t('payment.callback.timeoutDetails.continue', 'Go to Dashboard')}
            </button>
          </div>
        );

      case 'success':
        return (
          <div className="callback-content success">
            <div className="success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#4CAF50"/>
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>{t('payment.callback.successDetails.title', 'Payment Successful!')}</h1>
            <p>{t('payment.callback.successDetails.message', 'Thank you for your payment. Your transaction has been completed successfully.')}</p>
            {details && (
              <div className="payment-details">
                <p><strong>{t('payment.callback.transactionId', 'Transaction ID')}:</strong> {details.transactionId}</p>
                <p><strong>{t('payment.callback.status', 'Status')}:</strong> {details.status}</p>
              </div>
            )}
            <p>{t('payment.callback.successDetails.description', 'You can now enjoy all the premium features.')}</p>
            <button className="continue-button success" onClick={handleContinue}>
              {t('payment.callback.successDetails.continue', 'Continue to Dashboard')}
            </button>
          </div>
        );

      case 'cancelled':
        return (
          <div className="callback-content cancelled">
            <div className="cancelled-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#FF9800"/>
                <path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>{t('payment.callback.cancelledDetails.title', 'Payment Cancelled')}</h1>
            <p>{t('payment.callback.cancelledDetails.message', 'Your payment was cancelled. No charges have been made to your account.')}</p>
            <p>{t('payment.callback.cancelledDetails.description', 'You can try again or choose a different payment method.')}</p>
            <button className="continue-button cancelled" onClick={handleContinue}>
              {t('payment.callback.cancelledDetails.continue', 'Back to Pricing')}
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="callback-content error">
            <div className="error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#F44336"/>
                <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>{t('payment.callback.failedDetails.title', 'Payment Failed')}</h1>
            <p>{message}</p>
            <p>{t('payment.callback.failedDetails.description', 'Please try again or contact our support team if the problem persists.')}</p>
            <button className="continue-button error" onClick={handleContinue}>
              {t('payment.callback.failedDetails.continue', 'Back to Pricing')}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="payment-callback-container">
      <div className="payment-callback-card">
        {renderContent()}
        {/* 支付状态轮询器 */}
        {showPoller && orderId && isPollerActive && (
          <PaymentStatusPoller
            orderId={orderId}
            onStatusUpdate={handleStatusUpdate}
            maxAttempts={15}
            interval={3000}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
